import { toast, ToastContentProps, ToastOptions } from "react-toastify";
import { createStore } from "zustand-x";

// Simple fallback logger - ensure no hooks are called
const safeLogger = {
  fatal: (msg: string) => {
    // Use direct console API to avoid any potential hook calls
    if (typeof msg === "string") {
      console.error(`[FATAL] ${msg}`);
    } else {
      console.error("[FATAL]", msg);
    }
  },
};

type SnackbarType = "success" | "info" | "warning" | "error";

type Snackbar = {
  type: SnackbarType;
  msg: string | ((props: ToastContentProps) => JSX.Element);
  id: string;
  options: Record<string, unknown>;
};

type SnackbarOptions = ToastOptions & {
  id?: string;
  autoClose?: number;
};

type NotificationStore = {
  snackbars: Snackbar[];
};

const notificationStore = createStore<NotificationStore>(
  {
    snackbars: [],
  },
  {
    name: "NotificationStore",
    devtools: true,
  }
);

// Store actions for direct usage
export const notificationActions = {
  // Add notification to the store
  addSnackbar: (type: SnackbarType, msg: string, options: SnackbarOptions = {}) => {
    const types: SnackbarType[] = ["success", "info", "warning", "error"];
    if (!types.includes(type)) {
      throw new Error("Invalid notification type");
    }

    const id = options.id || Math.random().toString(36).substring(2);

    // Display with toast - wrapped in try/catch to prevent hook errors
    try {
      toast[type](msg, {
        toastId: id,
        ...options,
      });
    } catch (error) {
      // Fallback to console if toast fails due to hook context issues
      console.warn(`[${type.toUpperCase()}] ${msg}`, error);
    }

    // Add to store using the underlying zustand store
    const currentSnackbars = notificationStore.store.getState().snackbars;
    notificationStore.store.setState({
      snackbars: [
        ...currentSnackbars,
        { type, msg, id, options: options as Record<string, unknown> },
      ],
    });

    return id;
  },

  // Remove a specific notification
  removeSnackbar: (id: string) => {
    const currentSnackbars = notificationStore.store.getState().snackbars;
    notificationStore.store.setState({
      snackbars: currentSnackbars.filter((k) => k.id !== id),
    });
  },

  // Remove all notifications
  removeAllSnackbars: () => {
    notificationStore.store.setState({ snackbars: [] });
  },

  // Dismiss a notification with toast
  dismissSnackbar: (id: string | "all") => {
    toast.dismiss(id);

    if (id === "all") {
      notificationStore.store.setState({ snackbars: [] });
    } else {
      const currentSnackbars = notificationStore.store.getState().snackbars;
      notificationStore.store.setState({
        snackbars: currentSnackbars.filter((k) => k.id !== id),
      });
    }
  },

  // Convenience method for success notifications
  success: (msg: string, options = {}) => {
    const id = Math.random().toString(36).substring(2);

    try {
      toast.success(msg, {
        toastId: id,
        ...options,
      });
    } catch (error) {
      console.warn(`[SUCCESS] ${msg}`, error);
    }

    const currentSnackbars = notificationStore.store.getState().snackbars;
    notificationStore.store.setState({
      snackbars: [
        ...currentSnackbars,
        { type: "success", msg, id, options: options as Record<string, unknown> },
      ],
    });

    return id;
  },

  // Convenience method for info notifications
  info: (msg: string | ((props: ToastContentProps) => JSX.Element), options = {}) => {
    const id = Math.random().toString(36).substring(2);

    try {
      toast.info(msg, {
        toastId: id,
        ...options,
      });
    } catch (error) {
      console.warn(`[INFO] ${typeof msg === "string" ? msg : "Component message"}`, error);
    }

    const currentSnackbars = notificationStore.store.getState().snackbars;
    notificationStore.store.setState({
      snackbars: [
        ...currentSnackbars,
        { type: "info", msg, id, options: options as Record<string, unknown> },
      ],
    });

    return id;
  },

  // Convenience method for warning notifications
  warning: (
    msg: string | ((props: ToastContentProps) => JSX.Element),
    options = { autoClose: 8000 }
  ) => {
    const id = Math.random().toString(36).substring(2);

    try {
      toast.warning(msg, {
        toastId: id,
        ...options,
      });
    } catch (error) {
      console.warn(`[WARNING] ${typeof msg === "string" ? msg : "Component message"}`, error);
    }

    const currentSnackbars = notificationStore.store.getState().snackbars;
    notificationStore.store.setState({
      snackbars: [
        ...currentSnackbars,
        { type: "warning", msg, id, options: options as Record<string, unknown> },
      ],
    });

    return id;
  },

  // Convenience method for error notifications
  error: (
    msg: string | ((props: ToastContentProps) => JSX.Element),
    options = { autoClose: 10000 }
  ) => {
    safeLogger.fatal(msg as string);

    const id = Math.random().toString(36).substring(2);

    try {
      toast.error(msg, {
        toastId: id,
        ...options,
      });
    } catch (error) {
      console.error(`[ERROR] ${typeof msg === "string" ? msg : "Component message"}`, error);
    }

    const currentSnackbars = notificationStore.store.getState().snackbars;
    notificationStore.store.setState({
      snackbars: [
        ...currentSnackbars,
        { type: "error", msg, id, options: options as Record<string, unknown> },
      ],
    });

    return id;
  },
};

export default notificationStore;
